// Quick-buy +/- controls on Weekly Food Offers cards (homepage).
//
// Round-trips to a custom AJAX endpoint (action=haegoziya_set_qty) that
// SETS the cart quantity for a given product (or product+variation, or
// product+weight-tier), rather than only adding. So + and − both stay in
// sync with the real cart on the server.
//
// WEIGHT TIERS: cards can show tier chips (e.g. 300g / 500g) via
// assets/src/js/modules/tier-chips.js. Each tier is its OWN cart line —
// the +/- control always operates on the CURRENTLY-ACTIVE chip, and the
// displayed count reflects that tier's quantity in the cart. Switching
// chips repaints the count from a per-tier map we keep on each card.
//
// CACHE-SAFETY: the homepage HTML is page-cached anonymously by Hostinger /
// LiteSpeed / most CDNs. So the cards render with data-qty-count="0" for
// every visitor (the PHP partial deliberately stamps 0). This module then
// hydrates the real per-user counts from a non-cacheable AJAX call after
// page load. Without this hydration, the cached HTML would show 0 forever
// and clicking + would overwrite the real cart back to 1.
//
// Each card carries:
//   data-product-id="123"
//   data-variation-id="124"   (0 for simple products)
//   data-attributes='{"attribute_pa_weight":"100g"}'
//   .tier-chip[data-tier-index][data-tier-price][data-tier-weight][data-tier-label]

(function () {
    // Live nonce for the cart AJAX endpoints. Seeded from the page, but the
    // page may be cache-served with a STALE nonce — so we refresh it from a
    // non-cacheable endpoint whenever a request is rejected, then retry. This
    // is what keeps the "+" working on page-cached homepages (main bug §6).
    let qtyNonce = (window.HAEGOZIYA && window.HAEGOZIYA.qtyNonce) || '';
    const getAjaxUrl = () => (window.HAEGOZIYA && window.HAEGOZIYA.ajaxUrl) || '/wp-admin/admin-ajax.php';

    async function refreshNonce() {
        try {
            const body = new FormData();
            body.append('action', 'haegoziya_cart_nonce');
            const res = await fetch(getAjaxUrl(), {
                method: 'POST',
                credentials: 'same-origin',
                cache: 'no-store',
                body,
            });
            if (!res.ok) return false;
            const json = await res.json();
            if (json && json.success && json.data && json.data.nonce) {
                qtyNonce = json.data.nonce;
                return true;
            }
        } catch (_) { /* offline / blocked — leave nonce as-is */ }
        return false;
    }

    function init() {
        const controls = document.querySelectorAll('[data-qty-control]');
        if (!controls.length) return;

        const handles = [];
        controls.forEach((control) => {
            const handle = setup(control);
            if (handle) handles.push(handle);
        });

        if (handles.length) hydrate(handles);
    }

    function setup(control) {
        // Data source: the .product-quick card for listing cards, or the
        // control element itself for ad-hoc placements like the quick-view
        // modal's qty row (which doesn't sit inside a .product-quick).
        const dataEl   = control.closest('.product-quick') || control;
        const countEl  = control.querySelector('[data-qty-count]');
        const minusBtn = control.querySelector('[data-qty-minus]');
        const plusBtn  = control.querySelector('[data-qty-plus]');
        if (!countEl || !plusBtn) return null;

        const tierChips = Array.from(dataEl.querySelectorAll('.tier-chip'));
        const hasTiers  = tierChips.length > 0;
        const tierQtys  = {}; // tierIndex -> qty currently in cart

        let count = parseInt(countEl.textContent, 10) || 0;
        let pending = false;

        const render = () => {
            countEl.textContent = String(count);
            if (minusBtn) minusBtn.disabled = pending || count <= 0;
            plusBtn.disabled = pending;
        };

        const activeChip = () =>
            dataEl.querySelector('.tier-chip.is-active') || tierChips[0] || null;

        // Read ids LAZILY each click so a single bound control (e.g. the
        // modal's qty row) can be repointed at a new product just by
        // mutating its data-* attributes — and so the +/- always acts on
        // whichever weight chip is currently selected.
        const readContext = () => {
            const productId   = parseInt(dataEl.dataset.productId || '0', 10);
            const variationId = parseInt(dataEl.dataset.variationId || '0', 10) || 0;
            let attributes    = {};
            try { attributes = JSON.parse(dataEl.dataset.attributes || '{}'); } catch (_) {}

            let tier = null;
            if (hasTiers) {
                // Listing cards: read the active weight chip in the card.
                const chip = activeChip();
                if (chip) {
                    tier = {
                        index:  parseInt(chip.dataset.tierIndex || '0', 10),
                        price:  chip.dataset.tierPrice  || '',
                        weight: chip.dataset.tierWeight || '',
                        label:  chip.dataset.tierLabel  || '',
                    };
                }
            } else if (dataEl.dataset.activeTierIndex !== undefined && dataEl.dataset.activeTierIndex !== '') {
                // Quick-view modal: the chosen tier is stashed on the control
                // itself by quickview.js (the chips live outside this element).
                tier = {
                    index:  parseInt(dataEl.dataset.activeTierIndex || '0', 10),
                    price:  dataEl.dataset.activeTierPrice  || '',
                    weight: dataEl.dataset.activeTierWeight || '',
                    label:  dataEl.dataset.activeTierLabel  || '',
                };
            }
            return { productId, variationId, attributes, tier };
        };

        const change = async (delta) => {
            if (pending) return;
            const { productId, variationId, attributes, tier } = readContext();
            if (!productId) return;        // unbound modal slot — bail
            const next = Math.max(0, count + delta);
            if (next === count) return;
            const previous = count;
            count = next;
            pending = true;
            render();
            try {
                const data = await setQty(productId, variationId, attributes, next, tier);
                if (data && typeof data.count === 'number') {
                    count = data.count;
                }
                if (hasTiers && tier) tierQtys[tier.index] = count;
                applyFragments(data && data.fragments);
                bumpHeaderCart();
            } catch (err) {
                count = previous; // rollback
            } finally {
                pending = false;
                render();
            }
        };

        plusBtn.addEventListener('click', (e) => { e.preventDefault(); change(+1); });
        if (minusBtn) {
            minusBtn.addEventListener('click', (e) => { e.preventDefault(); change(-1); });
        }

        // Weight chip switched → show THIS tier's cart quantity. Dispatched
        // by assets/src/js/modules/tier-chips.js on the .product-quick card.
        if (hasTiers) {
            dataEl.addEventListener('haegoziya:tier-changed', (e) => {
                if (pending) return;
                const idx = e.detail && typeof e.detail.index === 'number' ? e.detail.index : 0;
                count = tierQtys[idx] || 0;
                render();
            });
        }

        // Modal can ask us to reset the count to 0 when it loads a new
        // product into the same control. Hooked here so external callers
        // don't need to know the closure-local `count` variable.
        control.addEventListener('haegoziya:rebind-qty', () => {
            count = 0;
            pending = false;
            render();
        });

        render();

        // Pull the first context for hydration — even though we re-read
        // it on every click, hydrate() needs a snapshot to batch with.
        const ctx = readContext();
        return {
            productId:   ctx.productId,
            variationId: ctx.variationId,
            hasTiers,
            tierIndices: tierChips.map((c) => parseInt(c.dataset.tierIndex || '0', 10)),
            // Called by hydrate() with the real per-user counts from the server.
            // Skips when the user is mid-action so we don't clobber an in-flight
            // optimistic update.
            applyQtys(qtys) {
                if (pending) return;
                if (hasTiers) {
                    this.tierIndices.forEach((i) => {
                        const k = this.productId + ':' + this.variationId + ':' + i;
                        tierQtys[i] = qtys[k] || 0;
                    });
                    const chip = activeChip();
                    const ai   = chip ? parseInt(chip.dataset.tierIndex || '0', 10) : 0;
                    count = tierQtys[ai] || 0;
                } else {
                    const exactKey  = this.productId + ':' + this.variationId;
                    const simpleKey = this.productId + ':0';
                    count = (exactKey in qtys)
                        ? qtys[exactKey]
                        : (simpleKey in qtys ? qtys[simpleKey] : 0);
                }
                render();
            },
        };
    }

    // Batch-fetch real cart quantities for every card on the page, then
    // populate them. Also nudges Woo's own fragment refresh so the header
    // cart bubble updates from cached HTML.
    async function hydrate(handles) {
        const ajaxUrl = getAjaxUrl();
        // Cached HTML can ship without/with a stale nonce — mint a fresh one
        // up front so hydration (and the first click) authenticate cleanly.
        if (!qtyNonce) await refreshNonce();
        if (!qtyNonce) return;

        const body = new FormData();
        body.append('action', 'haegoziya_get_qtys');
        body.append('nonce', qtyNonce);
        handles.forEach((h) => {
            if (h.hasTiers && h.tierIndices.length) {
                // One key per weight tier so each cart line is counted on its own.
                h.tierIndices.forEach((i) => body.append('items[]', h.productId + ':' + h.variationId + ':' + i));
            } else {
                body.append('items[]', h.productId + ':' + h.variationId);
            }
        });

        let res;
        try {
            res = await fetch(ajaxUrl, {
                method: 'POST',
                credentials: 'same-origin',
                cache: 'no-store',
                body,
            });
        } catch (_) { return; }
        if (!res.ok) return;

        let json;
        try { json = await res.json(); } catch (_) { return; }
        if (!json || !json.success || !json.data || !json.data.qtys) return;

        const qtys = json.data.qtys;
        handles.forEach((h) => h.applyQtys(qtys));

        // Nudge Woo to repaint the header cart bubble using its own
        // fragment cache — handles the case where the cached homepage HTML
        // shows "0" in the header even though the user has items.
        if (window.jQuery) {
            window.jQuery(document.body).trigger('wc_fragment_refresh');
        }
    }

    async function setQty(productId, variationId, attributes, quantity, tier) {
        const ajaxUrl = getAjaxUrl();

        const buildBody = () => {
            const body = new FormData();
            body.append('action', 'haegoziya_set_qty');
            body.append('nonce', qtyNonce);
            body.append('product_id', String(productId));
            body.append('variation_id', String(variationId));
            body.append('quantity', String(quantity));
            for (const [k, v] of Object.entries(attributes || {})) {
                body.append('attributes[' + k + ']', v);
            }
            if (tier) {
                body.append('tier_index',  String(tier.index));
                body.append('tier_price',  String(tier.price));
                body.append('tier_weight', String(tier.weight));
                body.append('tier_label',  String(tier.label));
            }
            return body;
        };

        // First attempt. If the nonce is missing or stale the server replies
        // 403 (check_ajax_referer) — refresh the nonce once and retry so a
        // page-cached homepage doesn't leave the "+" permanently dead.
        let res = await fetch(ajaxUrl, { method: 'POST', credentials: 'same-origin', body: buildBody() });
        if ((res.status === 403 || res.status === 400) && await refreshNonce()) {
            res = await fetch(ajaxUrl, { method: 'POST', credentials: 'same-origin', body: buildBody() });
        }
        if (!res.ok) throw new Error('set_qty failed: ' + res.status);
        const json = await res.json();
        if (!json || !json.success) throw new Error('set_qty rejected');
        return json.data;
    }

    function applyFragments(fragments) {
        if (!fragments) return;
        for (const [selector, html] of Object.entries(fragments)) {
            document.querySelectorAll(selector).forEach((el) => {
                el.outerHTML = html;
            });
        }
    }

    function bumpHeaderCart() {
        if (window.jQuery) {
            window.jQuery(document.body).trigger('wc_fragment_refresh');
            window.jQuery(document.body).trigger('added_to_cart');
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
